import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, Trash2, Clock, Repeat, Mail, MessageSquare, Phone, CalendarClock, BellOff, MapPin, AlarmClock, Crown, ArrowRight, Car, Dumbbell } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FeatureGate } from "@/components/FeatureGate";
import TaskDialog from "@/components/TaskDialog";
import WishList from "@/components/WishList";
import CashMiles from "@/components/CashMiles";
import MoneyFlex from "@/components/MoneyFlex";
import UnifiedCalendar from "@/components/snap/UnifiedCalendar";

const CH_ICON = { email: Mail, sms: MessageSquare, voice: Phone };

function ChannelDots({ channels }) {
  if (!channels || channels.length === 0) {
    return <span className="flex items-center gap-1 font-mono text-xs text-[var(--text-2)]"><BellOff className="w-3.5 h-3.5" /> Silent</span>;
  }
  return (
    <div className="flex gap-1">
      {channels.map((c) => {
        const I = CH_ICON[c] || Mail;
        return <I key={c} className="w-3.5 h-3.5 text-[var(--text-2)]" />;
      })}
    </div>
  );
}

export default function PersonalMode({ tasks, reload, initialTab = "reminders" }) {
  const { user } = useAuth();
  const personal = user?.role === "admin" || user?.personal_active;
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [ptab, setPtab] = useState(["miles", "moneyflex"].includes(initialTab) ? initialTab : "reminders");
  const [mileMonth, setMileMonth] = useState(null);
  useEffect(() => { api.get("/cash-miles?period=month").then((r) => setMileMonth(r.data)).catch(() => {}); }, [ptab]);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [calendar, setCalendar] = useState(null);
  const todayIso = now.toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayIso);
  const [view, setView] = useState("day"); // day | overdue
  const [snoozeFor, setSnoozeFor] = useState(null);

  const reminders = tasks.filter((t) => t.kind === "task");
  const activeReminders = reminders.filter((t) => !t.completed).length;
  const capLimit = user?.reminders_limit ?? 10;
  const capped = !personal && activeReminders >= capLimit;
  const reminderDate = (t) => (t.next_run ? t.next_run.slice(0, 10) : t.start_date) || "";
  const dayReminders = reminders.filter((t) => reminderDate(t) === selectedDate);
  const overdueReminders = reminders.filter((t) => !t.completed && reminderDate(t) && reminderDate(t) < todayIso);
  const displayed = view === "overdue" ? overdueReminders : dayReminders;
  const isToday = selectedDate === todayIso;
  const pad = (n) => String(n).padStart(2, "0");
  const selDate = new Date(selectedDate + "T00:00:00");
  const selInView = selDate.getFullYear() === year && selDate.getMonth() + 1 === month;
  const selectedDay = view === "day" && selInView ? selDate.getDate() : null;
  const dateLabel = selDate.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
  const goToday = () => { setSelectedDate(todayIso); setView("day"); };
  const bills = tasks.filter((t) => t.kind === "bill");
  const taskSig = tasks.map((t) => `${t.id}:${t.next_run}:${t.completed}:${t.amount}`).join("|");

  const loadCalendar = useCallback((m, y) => {
    api.get(`/snapbudget/calendar?month=${m}&year=${y}`).then((r) => setCalendar(r.data)).catch(() => {});
  }, []);
  useEffect(() => { loadCalendar(month, year); }, [month, year, loadCalendar, taskSig]);

  const shiftMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const toggle = async (t) => { await api.put(`/tasks/${t.id}`, { completed: !t.completed }); reload(); };
  const remove = async (t) => { await api.delete(`/tasks/${t.id}`); toast.success("Deleted"); reload(); };
  const snoozeTo = async (t, iso) => {
    await api.put(`/tasks/${t.id}`, { start_date: iso, recurrence: t.recurrence });
    setSnoozeFor(null);
    toast.success("Reminder snoozed");
    reload();
  };
  const addDays = (n) => { const d = new Date(todayIso + "T00:00:00"); d.setDate(d.getDate() + n); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
  const openEdit = (t) => { setEditing(t); setDialog(true); };
  const openNew = () => { setEditing(null); setDialog(true); };
  const tryAdd = () => {
    if (capped) { toast.error(`Free plan is limited to ${capLimit} active reminders & to-dos. Upgrade to Premium for unlimited.`); return; }
    openNew();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="label-mono text-[var(--text-2)]">My personal profile</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none mt-2">
            Today, <span className="italic text-[var(--accent)]">handled</span>.
          </h1>
          {!personal && (
            <p data-testid="reminder-usage" className="label-mono text-[var(--text-2)] mt-3">
              {activeReminders}/{capLimit} active reminders · <Link to="/pricing" className="text-[var(--accent)] hover:underline">Upgrade to Premium for unlimited</Link>
            </p>
          )}
        </div>
        <button
          data-testid="quick-add-task"
          onClick={tryAdd}
          className="flex items-center gap-2 h-12 px-5 bg-[#1A1A18] text-white hover:bg-[var(--accent)] transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" /> Quick add
        </button>
      </div>

      <div className="flex gap-2 mb-8" data-testid="personal-tabs">
        <button data-testid="ptab-reminders" onClick={() => setPtab("reminders")} className={`h-10 px-4 text-sm border transition-colors ${ptab === "reminders" ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}>Reminders</button>
        <button data-testid="ptab-miles" onClick={() => setPtab("miles")} className={`flex items-center gap-2 h-10 px-4 text-sm border transition-colors ${ptab === "miles" ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}><Car className="w-4 h-4" /> Cash Miles</button>
        <button data-testid="ptab-moneyflex" onClick={() => setPtab("moneyflex")} className={`flex items-center gap-2 h-10 px-4 text-sm border transition-colors ${ptab === "moneyflex" ? "bg-[#1A1A18] text-white border-[#1A1A18]" : "bg-white border-[var(--line)] hover:border-[var(--accent)]"}`}><Dumbbell className="w-4 h-4" /> MoneyFlex</button>
      </div>

      {ptab === "miles" && <CashMiles />}
      {ptab === "moneyflex" && <MoneyFlex />}

      {ptab === "reminders" && (<>
      {mileMonth?.total_deduction > 0 && (
        <div data-testid="dashboard-mileage-card" className="mb-6 flex items-center justify-between rounded-xl border border-[var(--accent)]/30 bg-[var(--accent)]/5 px-5 py-3">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 grid place-items-center rounded-lg bg-[var(--accent)]/15"><Car className="w-4 h-4 text-[var(--accent)]" /></span>
            <div>
              <p className="text-sm font-semibold">Mileage tax credit this month</p>
              <p className="text-xs text-[var(--text-2)]">{mileMonth.total_miles} miles logged · ${mileMonth.rate}/mi</p>
            </div>
          </div>
          <div className="text-right">
            <p data-testid="dashboard-mileage-amount" className="font-mono text-xl font-bold text-[var(--accent)]">${(mileMonth.total_deduction).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <button onClick={() => setPtab("miles")} className="text-xs text-[var(--accent)] hover:underline">View Cash Miles →</button>
          </div>
        </div>
      )}
      {capped && (
        <div data-testid="upgrade-nudge" className="mb-8 border border-[var(--accent)]/40 bg-[var(--accent)]/5 p-5 rounded-lg flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-[var(--accent)]/15 grid place-items-center shrink-0"><Crown className="w-5 h-5 text-[var(--accent)]" /></div>
          <div className="flex-1">
            <p className="font-serif text-lg leading-tight">You've reached the Free limit of {capLimit} active reminders</p>
            <p className="text-sm text-[var(--text-2)] mt-1">Upgrade to <strong>Premium ($29.99/mo)</strong> for unlimited reminders & tasks, team boards, GPS check-ins and full history — start a 7-day free trial.</p>
          </div>
          <Link to="/pricing" data-testid="upgrade-nudge-cta" className="group h-11 px-5 bg-[var(--accent)] text-white text-sm font-medium flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shrink-0 rounded">
            See plans <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Reminders */}
        <section className="lg:col-span-6 border border-[var(--line)] bg-white">
          <div className="flex items-center justify-between p-6 border-b border-[var(--line)]">
            <h2 className="font-serif text-2xl tracking-tight flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[var(--accent)]" /> {view === "overdue" ? "Overdue" : isToday ? "Today's Reminders" : `Reminders · ${dateLabel}`}
            </h2>
            <div className="flex items-center gap-3">
              {overdueReminders.length > 0 && (
                <button data-testid="reminders-overdue" onClick={() => setView(view === "overdue" ? "day" : "overdue")} className={`label-mono hover:underline ${view === "overdue" ? "text-[var(--text-2)]" : "text-[var(--danger)]"}`}>
                  {view === "overdue" ? "Back" : `Overdue (${overdueReminders.length})`}
                </button>
              )}
              {view === "day" && !isToday && <button data-testid="reminders-today" onClick={goToday} className="label-mono text-[var(--accent)] hover:underline">Today</button>}
              <span className="label-mono text-[var(--text-2)]">{displayed.length} {view === "overdue" ? "overdue" : "on this day"}</span>
            </div>
          </div>
          <div className="divide-y divide-[var(--line)]">
            {view === "day" && overdueReminders.length > 0 && (
              <div data-testid="overdue-box" className="m-4 p-4 rounded" style={{ backgroundColor: "rgba(220,38,38,0.06)", border: "1px solid rgba(220,38,38,0.35)" }}>
                <div className="flex items-center justify-between">
                  <span className="font-medium text-[var(--danger)] flex items-center gap-2"><AlarmClock className="w-4 h-4" /> {overdueReminders.length} overdue</span>
                  <button data-testid="overdue-review" onClick={() => setView("overdue")} className="text-xs text-[var(--danger)] hover:underline">Review all</button>
                </div>
                <ul className="mt-2 space-y-1">
                  {overdueReminders.slice(0, 4).map((t) => (
                    <li key={t.id} className="text-xs text-[var(--text-2)] flex justify-between gap-2"><span className="truncate">{t.title}</span><span className="font-mono shrink-0">{reminderDate(t)}</span></li>
                  ))}
                  {overdueReminders.length > 4 && <li className="text-xs text-[var(--text-2)]">+{overdueReminders.length - 4} more…</li>}
                </ul>
              </div>
            )}
            {displayed.length === 0 && <Empty text={view === "overdue" ? "Nothing overdue — you're all caught up." : isToday ? "Nothing scheduled today. Tap a calendar day to view another date." : "No reminders on this day. Tap another day or hit Quick add."} />}
            {displayed.map((t) => (
              <div key={t.id} data-testid="task-row" className="flex items-center gap-4 p-4 sm:p-5 group">
                <button
                  data-testid="task-toggle"
                  onClick={() => toggle(t)}
                  className={`w-6 h-6 border-2 flex items-center justify-center shrink-0 transition-colors ${t.completed ? "bg-[var(--accent)] border-[var(--accent)] animate-pop" : "border-[var(--text-2)] hover:border-[var(--accent)]"}`}
                >
                  {t.completed && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                </button>
                <div className="min-w-0 flex-1">
                  <p className={`font-medium truncate ${t.completed ? "line-through text-[var(--text-2)]" : ""}`}>{t.title}</p>
                  <div className="flex items-center gap-3 mt-1 text-[var(--text-2)]">
                    <span className="flex items-center gap-1 font-mono text-xs"><Clock className="w-3 h-3" /> {t.time}</span>
                    <span className="flex items-center gap-1 font-mono text-xs"><Repeat className="w-3 h-3" /> {t.recurrence_label}</span>
                    <ChannelDots channels={t.channels} />
                  </div>
                  {t.address && <a data-testid="reminder-map-link" href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t.address)}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 mt-1 text-xs text-[var(--accent)] hover:underline w-fit"><MapPin className="w-3 h-3" /> {t.address}</a>}
                </div>
                <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                  <div className="relative">
                    <button data-testid="task-snooze" onClick={() => setSnoozeFor(snoozeFor === t.id ? null : t.id)} title="Snooze" className="px-2 h-8 text-xs border border-[var(--line)] hover:border-[var(--accent)] transition-colors flex items-center gap-1"><AlarmClock className="w-3.5 h-3.5" /> Snooze</button>
                    {snoozeFor === t.id && (
                      <div className="absolute right-0 top-9 z-20 w-44 bg-white border border-[var(--line)] shadow-lg p-1" data-testid="snooze-menu">
                        <button data-testid="snooze-tomorrow" onClick={() => snoozeTo(t, addDays(1))} className="w-full text-left px-2 h-8 text-xs hover:bg-[#f9f9f7]">Tomorrow</button>
                        <button data-testid="snooze-nextweek" onClick={() => snoozeTo(t, addDays(7))} className="w-full text-left px-2 h-8 text-xs hover:bg-[#f9f9f7]">Next week</button>
                        <div className="px-2 py-1"><input type="date" data-testid="snooze-date" onChange={(e) => e.target.value && snoozeTo(t, e.target.value)} className="w-full text-xs border border-[var(--line)] px-1 h-7" /></div>
                      </div>
                    )}
                  </div>
                  <button data-testid="task-edit" onClick={() => openEdit(t)} className="px-2 h-8 text-xs border border-[var(--line)] hover:border-[var(--accent)] transition-colors">Edit</button>
                  <button data-testid="task-delete" onClick={() => remove(t)} className="w-8 h-8 flex items-center justify-center border border-[var(--line)] hover:border-[var(--danger)] hover:text-[var(--danger)] transition-colors">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Pulse calendar */}
        <div className="lg:col-span-6">
          <FeatureGate locked={!personal} testid="gate-calendar" title="Pulse calendar" tier="Premium" blurb="See personal, business & bill due-dates in one timeline.">
            <UnifiedCalendar
              data={calendar} month={month} year={year} theme="pulse" title="Pulse"
              testidPrefix="personal-cal" className="w-full"
              selectedDay={selectedDay}
              onSelectDay={(day) => { setSelectedDate(`${year}-${pad(month)}-${pad(day)}`); setView("day"); }}
              onPrev={() => shiftMonth(-1)} onNext={() => shiftMonth(1)}
            />
          </FeatureGate>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-6">
          <FeatureGate locked={!personal} testid="gate-wishlist" title="Wish list & savings" tier="Premium" blurb="Track savings goals and fund your wishes.">
            <WishList />
          </FeatureGate>
        </div>
      </div>
      </>)}

      <TaskDialog open={dialog} onClose={() => setDialog(false)} onSaved={reload} task={editing} />
    </div>
  );
}

function Empty({ text }) {
  return <div className="p-10 text-center text-sm text-[var(--text-2)]">{text}</div>;
}
